import { defineComponent } from "vue";
import { Container } from "aurelia-dependency-injection";
import moment from "moment";

import { PatientService } from "../../../services/patient.service";
import { ToastrService } from "../../../services/toastr.service";
import { formatDate, formatPhone, formatZipCode } from "../../../common/vue-helpers/modifiers/value-modifier";
import { ApplicationEnum, Gender } from "../../../enums/enums";

import FormSection from "../../vue-custom-elements/FormSection/FormSection.vue";

export default defineComponent({
    inject: ["dialogRef"],

    components: {
        FormSection
    },

    data() {
        return {
            _patientsService: null,
            _toastrService: null,
            dialogReference: this.dialogRef as any,
            patientDetails: null,
            application: "",
            userGender: "N/A",
            carePeriodStart: "",
            carePeriodEnd: "",
            startOfCare: "",
            carePeriod: "N/A",
            carePeriodTitle: "Care Period",
            fullName: "",
            DOB: "",
            primaryPhone: "",
            emergencyPhone: "",
            isLoading: true,
            formatDate,
            formatPhone,
            formatZipCode
        };
    },
    computed: {
        profileTitle(): string {
            return Number(this.application) === ApplicationEnum.HomeCare ? "Client Information" : "Patient Information";
        }
    },

    async created() {
        this._patientsService = Container.instance.get(PatientService);
        this._toastrService = Container.instance.get(ToastrService);
        try {
            const data = Object.assign({}, this.dialogReference.data.patientData);
            const payload = {
                agencyId: data.AgencyId,
                application:
                    data.Application === ApplicationEnum.AxxessHospice
                        ? ApplicationEnum.AxxessHospiceFE
                        : data.Application,
                patientId: data.Id
            };
            this.application = data.Application;
            switch (data.Application) {
                case ApplicationEnum.AgencyCore:
                    this.carePeriodTitle = "Episode Period";
                    break;
                case ApplicationEnum.HomeCare:
                    this.carePeriodTitle = "Care Period";
                    break;
                case ApplicationEnum.AxxessPalliative:
                    this.carePeriodTitle = "Admission Period";
                    break;
                case ApplicationEnum.AxxessHospice:
                    this.carePeriodTitle = "Benefit Period";
                    break;
                default:
                    this.carePeriodTitle = "Care Period";
                    break;
            }
            const response = await this._patientsService.getPatientsProfile(payload);
            if (response) {
                this.isLoading = false;
                if (response.IsSuccessful) {
                    this.patientDetails = response.data;
                    this.fullName =
                        this.patientDetails.LastName +
                        ", " +
                        this.patientDetails.FirstName +
                        (this.patientDetails?.MiddleInitial ? " " + this.patientDetails.MiddleInitial : "");
                    if (this.patientDetails.CarePeriod) {
                        [this.carePeriodStart, this.carePeriodEnd] = this.patientDetails.CarePeriod.split("-");
                        this.carePeriodStart = formatDate(this.carePeriodStart);
                        this.carePeriodEnd = formatDate(this.carePeriodEnd);
                        const endDateYear = moment(this.carePeriodEnd).year();
                        if (endDateYear == 9999) {
                            this.carePeriodEnd = "Open";
                        } else {
                            this.carePeriodEnd = formatDate(this.carePeriodEnd);
                        }
                        this.carePeriod = this.carePeriodStart + " - " + this.carePeriodEnd;
                    }
                    if (
                        this.application == ApplicationEnum.AxxessHospice.toString() ||
                        this.application == ApplicationEnum.AxxessPalliative.toString()
                    ) {
                        this.userGender = Gender.getDescriptionUsingValue(Number(this.patientDetails.Gender));
                    } else {
                        this.userGender = this.patientDetails.Gender || "N/A";
                    }
                    this.DOB = this.patientDetails.DOB ? formatDate(this.patientDetails.DOB) : "N/A";
                    this.primaryPhone = this.patientDetails.PhoneNumber
                        ? formatPhone(this.patientDetails.PhoneNumber)
                        : "N/A";
                    this.emergencyPhone = this.patientDetails.EmergencyPrimaryContactPhone
                        ? formatPhone(this.patientDetails.EmergencyPrimaryContactPhone)
                        : "N/A";
                    this.startOfCare = this.patientDetails.StartOfCare
                        ? formatDate(this.patientDetails.StartOfCare)
                        : "N/A";
                } else {
                    this.fullName = "-";
                    this.carePeriodEnd = "-";
                    this.carePeriodStart = "-";
                    this.carePeriod = "-";
                    this.userGender = "-";
                    this.DOB = "-";
                    this.primaryPhone = "-";
                    this.emergencyPhone = "-";
                    this.startOfCare = "-";
                    this._toastrService.error({
                        title: `Error`,
                        message: `There was a problem while loading the Patient Information. Please try again. If the problem persists, please report it.`
                    });
                }
            }
        } catch (err) {
            this._toastrService.error({
                title: `Error`,
                message: `There was a problem while loading the Patient Information. Please try again. If the problem persists, please report it.`
            });
            this.cancel();
        }
    },

    methods: {
        cancel() {
            this.dialogReference.close("cancel");
        }
    }
});
